import { useState } from 'react';
import { T } from '../theme';
import { Camera, FileInput, ChevronDown } from 'lucide-react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import ExpenseForm from './ExpenseForm';
import { HelpTip } from './HelpTip';
import { TIPS } from '../help/content';
import { useReceiptScanner } from '../hooks/useReceiptScanner';
import { useEffect } from 'react';

const ASPECT_RATIOS = [
  { key: 'portrait',  label: 'Portrait',  w: 2, h: 5 },
  { key: 'square',    label: 'Square',    w: 1, h: 1 },
  { key: 'landscape', label: 'Landscape', w: 8.5, h: 11 },
  { key: 'legal',     label: 'Legal',     w: 14, h: 8.5 },

];

function AspectIcon({ w, h, active }) {
  const maxDim = 14;
  const ratio = w / h;
  const rw = ratio >= 1 ? maxDim : Math.round(maxDim * ratio);
  const rh = ratio >= 1 ? Math.round(maxDim / ratio) : maxDim;
  return (
    <svg width={20} height={20} viewBox="0 0 20 20" style={{ display: 'block' }}>
      <rect
        x={(20 - rw) / 2} y={(20 - rh) / 2}
        width={rw} height={rh} rx={1.5}
        fill="none"
        stroke={active ? '#fff' : 'rgba(255,255,255,0.5)'}
        strokeWidth={1.8}
      />
    </svg>
  );
}

export default function ExpenseEntryView({
  onSave,
  onCancel,
  categories,
  addCategory,
  checkLimit,
  showUpgradePrompt,
  refreshUsage,
  autoStartCamera,
  targetReport,
  expenseFiles = [],
  onReportChange,
}) {
  const {
    videoRef, canvasRef, cropImgRef,
    showCamera, cameraError, videoReady, torchOn, torchSupported,
    analyzing, pendingScan, crop, setCrop, capturedImage,
    extractError, newExpenseForm,
    startCamera, stopCamera, toggleTorch,
    capturePhotoWithRatio, handleImageFile, applyCropAndScan, retakeScan, resetScanState,
  } = useReceiptScanner({ checkLimit, showUpgradePrompt, refreshUsage });

  const [aspectRatioKey, setAspectRatioKey] = useState(
    () => localStorage.getItem('scan_aspect_ratio') ?? 'portrait'
  );
  const [cropMode, setCropMode] = useState(false);
  const aspectRatio = ASPECT_RATIOS.find(r => r.key === aspectRatioKey) ?? ASPECT_RATIOS[0];

  const setAspect = (key) => {
    setAspectRatioKey(key);
    localStorage.setItem('scan_aspect_ratio', key);
  };

  useEffect(() => {
    if (autoStartCamera) startCamera();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf';
    input.onchange = (e) => { if (e.target.files[0]) handleImageFile(e.target.files[0]); };
    input.click();
  };

  const handleFormSave = (formData) => {
    resetScanState();
    setCropMode(false);
    onSave(formData);
  };

  const inScanPipeline = showCamera || pendingScan != null || analyzing;

  return (
    <div style={{ padding: '20px', fontFamily: T.fontBody }}>

      {/* ── Scan / upload buttons ───────────────────────────────── */}
      {!inScanPipeline && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <button onClick={startCamera} style={scanBtnStyle}>
              <Camera size={20} /> Scan Receipt <HelpTip content={TIPS.scan} placement="top" />
            </button>
            <button onClick={handleFileUpload} style={scanBtnStyle}>
              <FileInput size={20} /> Select File
            </button>
          </div>
          <div style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 12 }}>
            Or enter details manually below
          </div>
          <div style={{ textAlign: 'center', color: '#B0BEC5', fontSize: 12, marginTop: 6 }}>
            Tip: Email receipts to{' '}
            <span style={{ fontFamily: T.fontMono, color: T.tealDeep }}>receipts@myspend.info</span>
            {' '}to scan without opening the app (available on Starter/Pro plans).
          </div>
        </div>
      )}

      {/* ── Camera viewfinder (full-screen overlay) ─────────────── */}
      {showCamera && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: '#000', display: 'flex', flexDirection: 'column',
        }}>
          {/* Header */}
          <div style={{ flexShrink: 0, padding: '12px 16px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                onClick={stopCamera}
                style={{
                  background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8,
                  width: 36, height: 36, fontSize: 20, cursor: 'pointer', color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}
              >×</button>
              <span style={{ color: '#fff', fontWeight: 800, fontSize: 17, flex: 1 }}>Scan Receipt</span>

              {/* Aspect ratio toggles */}
              <div style={{ display: 'flex', gap: 6 }}>
                {ASPECT_RATIOS.map(r => (
                  <button
                    key={r.key}
                    onClick={() => setAspect(r.key)}
                    title={r.label}
                    style={{
                      width: 36, height: 36, borderRadius: 8, border: 'none', cursor: 'pointer',
                      background: aspectRatioKey === r.key ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <AspectIcon w={r.w} h={r.h} active={aspectRatioKey === r.key} />
                  </button>
                ))}
              </div>
            </div>
            {/* Report picker */}
            {expenseFiles.length > 0 && onReportChange && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', whiteSpace: 'nowrap' }}>Adding to:</span>
                <div style={{ position: 'relative', flex: 1 }}>
                  <select
                    value={targetReport ?? ''}
                    onChange={e => onReportChange(e.target.value)}
                    style={{
                      width: '100%', padding: '6px 28px 6px 10px', borderRadius: 8,
                      border: '1.5px solid rgba(255,255,255,0.3)', fontSize: 12, fontWeight: 700,
                      background: 'rgba(255,255,255,0.15)', color: '#fff',
                      appearance: 'none', cursor: 'pointer', colorScheme: 'dark',
                    }}
                  >
                    {expenseFiles.map(f => <option key={f.name} value={f.name}>{f.name}</option>)}
                  </select>
                  <ChevronDown size={12} color="rgba(255,255,255,0.7)" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                </div>
              </div>
            )}
          </div>

          {/* Video area */}
          <div style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', padding: '0 16px',
          }}>
            {cameraError ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#EF4444' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📷</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#fff', marginBottom: 8 }}>Camera Error</div>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 20 }}>{cameraError}</div>
                <button onClick={stopCamera} style={tealBtnStyle}>Try Upload Instead</button>
              </div>
            ) : (
              <div style={{
                aspectRatio: `${aspectRatio.w} / ${aspectRatio.h}`,
                maxWidth: '100%', maxHeight: '100%',
                position: 'relative', borderRadius: 12, overflow: 'hidden',
              }}>
                <video
                  ref={videoRef}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  autoPlay playsInline muted
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />

                {/* Corner guides */}
                {videoReady && (
                  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                    {[
                      { top: 0, left: 0 },
                      { top: 0, right: 0 },
                      { bottom: 0, left: 0 },
                      { bottom: 0, right: 0 },
                    ].map((pos, i) => {
                      const isRight = 'right' in pos, isBottom = 'bottom' in pos;
                      return (
                        <div key={i} style={{ position: 'absolute', ...pos, width: 24, height: 24 }}>
                          <div style={{
                            position: 'absolute',
                            top: isBottom ? 'auto' : 0, bottom: isBottom ? 0 : 'auto',
                            left: isRight ? 'auto' : 0, right: isRight ? 0 : 'auto',
                            width: 24, height: 3, background: 'rgba(255,255,255,0.8)', borderRadius: 2,
                          }} />
                          <div style={{
                            position: 'absolute',
                            top: isBottom ? 'auto' : 0, bottom: isBottom ? 0 : 'auto',
                            left: isRight ? 'auto' : 0, right: isRight ? 0 : 'auto',
                            width: 3, height: 24, background: 'rgba(255,255,255,0.8)', borderRadius: 2,
                          }} />
                        </div>
                      );
                    })}
                  </div>
                )}

                {!videoReady && (
                  <div style={{
                    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', background: 'rgba(0,0,0,0.7)',
                  }}>
                    <div style={{ textAlign: 'center', color: '#fff' }}>
                      <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
                      <div style={{ fontSize: 14 }}>Starting camera...</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer controls */}
          <div style={{
            flexShrink: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12,
            padding: '16px 16px 32px',
          }}>
            <button onClick={stopCamera} style={cancelOverlayBtnStyle}>Cancel</button>
            <button
              onClick={() => capturePhotoWithRatio(aspectRatio.w / aspectRatio.h)}
              disabled={!videoReady}
              style={{ ...tealBtnStyle, opacity: videoReady ? 1 : 0.6, cursor: videoReady ? 'pointer' : 'not-allowed' }}
            >
              {videoReady ? 'Capture' : 'Loading...'}
            </button>
            {(torchSupported || ('ontouchstart' in window)) && (
              <button
                onClick={toggleTorch}
                title={torchOn ? 'Turn off flash' : 'Turn on flash'}
                style={{
                  padding: '12px 16px', borderRadius: 8, cursor: 'pointer', lineHeight: 1, fontSize: 18,
                  border: torchOn ? 'none' : '1.5px solid #fff',
                  background: torchOn ? '#F2A93B' : 'rgba(0,0,0,0.5)', color: '#fff',
                }}
              >
                🔦
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Crop / confirm step (full-screen overlay) ──────────── */}
      {pendingScan && !analyzing && !newExpenseForm && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: '#fff', display: 'flex', flexDirection: 'column',
        }}>
          {/* Header */}
          <div style={{ flexShrink: 0, padding: '12px 16px', borderBottom: '1px solid #E5E7EB' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                onClick={retakeScan}
                style={{
                  background: '#F3F4F6', border: 'none', borderRadius: 8,
                  width: 36, height: 36, fontSize: 20, cursor: 'pointer', color: '#374151',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}
              >×</button>
              <span style={{ fontWeight: 800, fontSize: 17, color: '#111', flex: 1 }}>Review & Crop</span>
            </div>
            {/* Report picker */}
            {expenseFiles.length > 0 && onReportChange && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                <span style={{ fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' }}>Adding to:</span>
                <div style={{ position: 'relative', flex: 1 }}>
                  <select
                    value={targetReport ?? ''}
                    onChange={e => onReportChange(e.target.value)}
                    style={{
                      width: '100%', padding: '6px 28px 6px 10px', borderRadius: 8,
                      border: `1.5px solid #D1D5DB`, fontSize: 12, fontWeight: 700,
                      background: '#F9FAFB', color: '#111',
                      appearance: 'none', cursor: 'pointer',
                    }}
                  >
                    {expenseFiles.map(f => <option key={f.name} value={f.name}>{f.name}</option>)}
                  </select>
                  <ChevronDown size={12} color="#6B7280" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
                </div>
              </div>
            )}
          </div>

          {/* Image area */}
          <div style={{
            flex: 1, overflow: 'hidden',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '8px 16px', background: '#F9FAFB',
          }}>
            {capturedImage ? (
              cropMode ? (
                <ReactCrop crop={crop} onChange={c => setCrop(c)}
                  style={{ maxWidth: '100%', maxHeight: '100%', display: 'flex' }}>
                  <img
                    ref={cropImgRef}
                    src={capturedImage}
                    alt="Receipt preview"
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block', borderRadius: 8 }}
                  />
                </ReactCrop>
              ) : (
                <img
                  ref={cropImgRef}
                  src={capturedImage}
                  alt="Receipt preview"
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block', borderRadius: 8 }}
                />
              )
            ) : (
              <div style={{
                width: 160, height: 200, borderRadius: 12, background: '#F3F4F6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '2px dashed #D1D5DB',
              }}>
                <div style={{ textAlign: 'center', color: '#6B7280' }}>
                  <div style={{ fontSize: 40, marginBottom: 6 }}>📄</div>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>PDF Document</div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            flexShrink: 0, padding: '12px 16px 32px',
            borderTop: '1px solid #E5E7EB', background: '#fff',
          }}>
            {cropMode && (
              <div style={{ fontSize: 12, color: '#9CA3AF', textAlign: 'center', marginBottom: 10 }}>
                Drag to select the area to keep
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={applyCropAndScan} style={{ ...tealBtnStyle, flex: 1, borderRadius: 10, padding: '13px' }}>
                {cropMode && crop?.width ? 'Crop & Analyze' : 'Analyze Receipt'}
              </button>
              {capturedImage && !cropMode && (
                <button onClick={() => setCropMode(true)} style={outlineBtnStyle}>Crop Image</button>
              )}
              <button onClick={() => { retakeScan(); setCropMode(false); }} style={outlineBtnStyle}>Retake</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Analyzing spinner ───────────────────────────────────── */}
      {analyzing && (
        <div style={{ textAlign: 'center', color: '#0A7C8A', fontSize: 14, padding: '40px 0' }}>
          Analyzing receipt...
        </div>
      )}

      {/* ── Error message ───────────────────────────────────────── */}
      {extractError && !analyzing && (
        <div style={{ color: '#EF4444', fontSize: 13, textAlign: 'center', marginBottom: 16 }}>
          {extractError}
        </div>
      )}

      {/* ── Receipt image preview after scan ───────────────────── */}
      {!pendingScan && capturedImage && !analyzing && (
        <img src={capturedImage} alt="Receipt" style={{ width: '100%', borderRadius: 12, marginBottom: 16 }} />
      )}

      {/* ── PDF placeholder after scan (no image) ──────────────── */}
      {!pendingScan && !capturedImage && newExpenseForm && !analyzing && (
        <div style={{
          width: '100%', height: 200, borderRadius: 12, background: '#F3F4F6',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 16, border: '2px dashed #D1D5DB',
        }}>
          <div style={{ textAlign: 'center', color: '#6B7280' }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>📄</div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>PDF Document</div>
            <div style={{ fontSize: 13 }}>No preview available for PDF files</div>
          </div>
        </div>
      )}

      {/* ── Expense form ────────────────────────────────────────── */}
      {!inScanPipeline && (
        <ExpenseForm
          initialForm={newExpenseForm}
          onSave={handleFormSave}
          onCancel={onCancel}
          categories={categories}
          addCategory={addCategory}
        />
      )}
    </div>
  );
}

const scanBtnStyle = {
  flex: 1, padding: '16px', borderRadius: 12, border: '1.5px solid #E5E7EB',
  background: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', color: '#6B7280',
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
};

const tealBtnStyle = {
  padding: '12px 24px', borderRadius: 8, border: 'none',
  background: '#0A7C8A', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
};

const cancelOverlayBtnStyle = {
  padding: '12px 24px', borderRadius: 8, border: '1.5px solid #fff',
  background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
};

const outlineBtnStyle = {
  padding: '11px 16px', borderRadius: 10, border: '1.5px solid #D1D5DB',
  background: '#fff', color: '#374151', fontSize: 14, fontWeight: 600,
  cursor: 'pointer', whiteSpace: 'nowrap',
};
